import type { ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { Icon } from "@core/assets";
import { Button } from "./Button";
import { cn } from "@core/utils";

interface RepeatableRowsProps<T> {
  /** The rows, in order. Each needs a stable key, so removing one never re-uses another's state. */
  items: readonly T[];
  getKey: (item: T, index: number) => string;
  /** The row's own content (a field, two fields). The remove button is added around it. */
  renderRow: (item: T, index: number) => ReactNode;
  onAdd: () => void;
  onRemove: (index: number) => void;
  /** « Ajouter une option ». */
  addLabel: string;
  /** Below this many rows the remove buttons are disabled (two options at least). */
  minRows?: number;
  /** Accessible name of each remove button: « Retirer l'option 2 ». */
  removeLabel: (index: number) => string;
  isDisabled?: boolean;
  className?: string;
}

/**
 * A list the person grows and shrinks: each row has a × to remove it and « + Ajouter … » adds an
 * empty one (Figma: the options of a single-choice question). Presentational: the caller owns the
 * rows and decides what an empty row is. The × is disabled at the minimum so the list can never
 * shrink below what the API accepts.
 */
export function RepeatableRows<T>({
  items,
  getKey,
  renderRow,
  onAdd,
  onRemove,
  addLabel,
  minRows = 1,
  removeLabel,
  isDisabled,
  className,
}: RepeatableRowsProps<T>) {
  const canRemove = items.length > minRows && !isDisabled;

  return (
    <div className={cn("flex flex-col gap-sm", className)}>
      <ul className="flex flex-col gap-sm">
        {items.map((item, index) => (
          <li key={getKey(item, index)} className="flex items-start gap-sm">
            <div className="min-w-0 flex-1">{renderRow(item, index)}</div>
            <AriaButton
              onPress={() => {
                onRemove(index);
              }}
              isDisabled={!canRemove}
              aria-label={removeLabel(index)}
              className={cn(
                "mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full",
                "border border-(--border-default) bg-surface-card text-(--text-secondary)",
                "outline-none hover:bg-ink-50 pressed:bg-ink-100",
                "focus-visible:ring-2 focus-visible:ring-focus-ring",
                "disabled:cursor-not-allowed disabled:text-(--text-disabled) disabled:hover:bg-surface-card"
              )}
            >
              <Icon name="close" size={16} />
            </AriaButton>
          </li>
        ))}
      </ul>
      <div>
        <Button variant="tertiary" onPress={onAdd} isDisabled={isDisabled}>
          <Icon name="plus" size={16} />
          {addLabel}
        </Button>
      </div>
    </div>
  );
}

export type { RepeatableRowsProps };
